import React, { useMemo, useState } from "react";
import { CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Select } from "antd";
import "./combo-filter.css";

const isActive = (value) => value != null && value !== "";

const labelOf = (options, raw) => {
  const found = (options || []).find((o) => String(o.value) === String(raw));
  return found?.label ?? String(raw);
};

const MiniSelect = ({
  autoFocus,
  defaultOpen,
  disabled,
  options,
  placeholder,
  value,
  onChange,
  width
}) => (
  <Select
    autoFocus={autoFocus}
    defaultOpen={defaultOpen}
    disabled={disabled}
    showSearch
    optionFilterProp="label"
    placeholder={placeholder}
    value={value}
    options={options}
    popupMatchSelectWidth={false}
    variant="borderless"
    suffixIcon={null}
    style={{ width }}
    onChange={onChange}
  />
);

/**
 * Поиск и чипы фильтров: строка поиска + набор фильтров-чипов.
 * kinds: [{ key, label, options: [{ value, label }], width? }]
 * value: { q, [kindKey] }, onChange(patch).
 */
const ComboFilter = ({ value, kinds = [], placeholder, onChange, className, ariaLabel }) => {
  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKind, setEditKind] = useState(null);

  const kindByKey = useMemo(
    () => new Map((kinds || []).map((k) => [k.key, k])),
    [kinds]
  );

  const chips = useMemo(
    () =>
      (kinds || [])
        .filter((k) => isActive(value?.[k.key]))
        .map((k) => ({
          key: k.key,
          kindLabel: k.label,
          valueLabel: labelOf(k.options, value[k.key])
        })),
    [kinds, value]
  );

  const used = new Set(chips.map((c) => c.key));
  const availableKinds = (kinds || [])
    .filter((k) => !used.has(k.key))
    .map((k) => ({ value: k.key, label: k.label }));
  const canAdd = availableKinds.length > 0;

  const applyKindValue = (kind, nextValue) => {
    onChange({ [kind]: nextValue == null || nextValue === "" ? null : nextValue });
  };

  const clearKind = (kind) => {
    applyKindValue(kind, null);
    if (editKind === kind) setEditKind(null);
  };

  const startAdd = () => {
    setEditKind(null);
    setDraftKind(null);
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
  };

  const pickDraftValue = (next) => {
    if (!draftKind) return;
    applyKindValue(draftKind, next);
    cancelAdd();
  };

  return (
    <div className={`combo-filter${className ? ` ${className}` : ""}`} role="group" aria-label={ariaLabel || "Поиск и фильтры"}>
      <span className="combo-filter__search-icon">
        <SearchOutlined />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="combo-filter__search-input"
        value={value?.q || ""}
        placeholder={placeholder}
        aria-label="Поиск"
        onChange={(e) => onChange({ q: e.target.value })}
      />
      {chips.map((chip) =>
        editKind === chip.key ? (
          <span key={chip.key} className="combo-filter__draft">
            <span className="combo-filter__chip-kind">{chip.kindLabel}</span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={kindByKey.get(chip.key)?.options || []}
              placeholder="Значение"
              value={value[chip.key]}
              width={kindByKey.get(chip.key)?.width || 160}
              onChange={(next) => {
                applyKindValue(chip.key, next);
                setEditKind(null);
              }}
            />
            <button
              type="button"
              className="combo-filter__chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKind(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="combo-filter__chip">
            <button
              type="button"
              className="combo-filter__chip-btn"
              title="Изменить значение"
              onClick={() => {
                cancelAdd();
                setEditKind(chip.key);
              }}
            >
              <span className="combo-filter__chip-kind">{chip.kindLabel}</span>
              <span className="combo-filter__chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="combo-filter__chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearKind(chip.key)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="combo-filter__draft">
          <MiniSelect
            autoFocus={!draftKind}
            defaultOpen={!draftKind}
            options={availableKinds}
            placeholder="Вид фильтра"
            value={draftKind}
            width={130}
            onChange={(kind) => setDraftKind(kind)}
          />
          <MiniSelect
            key={draftKind || "value"}
            autoFocus={Boolean(draftKind)}
            defaultOpen={Boolean(draftKind)}
            disabled={!draftKind}
            options={draftKind ? kindByKey.get(draftKind)?.options || [] : []}
            placeholder="Значение"
            width={draftKind ? kindByKey.get(draftKind)?.width || 160 : 160}
            onChange={pickDraftValue}
          />
          <button
            type="button"
            className="combo-filter__chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding ? (
        <button
          type="button"
          className="combo-filter__add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

export default ComboFilter;
